/**
 * Audit log. Every action AND every view is recorded — including who looked at which tenant's money, and
 * including this very screen.
 *
 * The immutability banner is not decoration: operators should know their own reads are recorded. That is a
 * deterrent, and equally a protection for honest staff, who can prove what they did and did not open.
 */
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useLoader } from "../lib/session";
import { dateTimeLong, relative } from "../lib/format";
import { C, card, EmptyState, ErrorState, Skeleton } from "../ui/primitives";

const ACTION_LABEL: Record<string, string> = {
  "view.overview": "opened the overview",
  "view.movements": "searched movements",
  "view.wallet": "viewed a wallet statement",
  "view.wallets": "browsed wallets",
  "view.recon_runs": "opened reconciliation",
  "view.recon_breaks": "reviewed breaks",
  "view.dlq": "opened the queues",
  "view.fees": "viewed fee rates",
  "view.flags": "reviewed flags",
  "view.audit": "read the audit log",
  "recon.break_resolved": "resolved a reconciliation break",
  "dlq.webhook_replay": "replayed a dead-lettered webhook",
  "dlq.webhook_replay_refused": "was refused a webhook replay",
  "dlq.outbox_replay": "replayed a platform delivery",
  "fee.change": "changed a fee rate",
  "refused.role": "was refused an action (viewer role)",
  "auth.sign_out": "signed out",
};

export function Audit() {
  const [params, setParams] = useSearchParams();
  const [cursor, setCursor] = useState<string | undefined>();
  const [older, setOlder] = useState<Awaited<ReturnType<typeof api.audit>>["rows"]>([]);

  const q = {
    tenantId: params.get("tenantId") ?? undefined,
    kind: params.get("kind") ?? undefined,
    cursor,
  };
  const { data, error, loading, reload } = useLoader(() => api.audit(q), [q.tenantId, q.kind, cursor]);
  const rows = [...older, ...(data?.rows ?? [])];

  const setFilter = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setOlder([]); setCursor(undefined); setParams(next);
  };

  return (
    <>
      <div style={{ border: "1px solid var(--line2)", background: "var(--accentsoft)", borderRadius: 13, padding: "11px 14px", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="2.2" strokeLinecap="round" style={{ flex: "none" }}>
          <rect x="4" y="10" width="16" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" />
        </svg>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--ink2)" }}>
          Append-only at the database. Nobody — including engineers — can edit or delete these rows. Views are recorded too, including yours: that protects honest staff as much as it deters.
        </span>
      </div>

      <div style={card}>
        <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--line)", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <select value={q.kind ?? ""} onChange={(e) => setFilter("kind", e.target.value)} style={sel}>
            <option value="">All activity</option>
            <option value="action">Actions only</option>
            <option value="view">Views only</option>
          </select>
          <input defaultValue={q.tenantId ?? ""} onKeyDown={(e) => { if (e.key === "Enter") setFilter("tenantId", (e.target as HTMLInputElement).value.trim()); }}
            placeholder="Tenant id — press Enter" style={{ ...sel, minWidth: 200 }} />
          {(q.tenantId || q.kind) && (
            <button type="button" onClick={() => { setOlder([]); setCursor(undefined); setParams(new URLSearchParams()); }}
              style={{ ...sel, color: C.accent, cursor: "pointer" }}>Clear filters</button>
          )}
        </div>

        {loading && rows.length === 0 ? <Skeleton rows={6} label="Loading the log…" />
          : error ? <ErrorState title="Couldn't load the audit log." body={error} onRetry={reload} />
          : rows.length === 0 ? (
            <EmptyState title="No entries match these filters."
              body="Nobody matching this filter touched this tenant in this window — and because the log is append-only, absence here is evidence, not a gap. Widen the range to check further back." />
          ) : (
            <>
              {rows.map((a) => {
                const isView = a.action.startsWith("view.");
                return (
                  <div key={a.id} style={{ display: "grid", gridTemplateColumns: "26px 1fr 210px", gap: 10, padding: "11px 16px", borderBottom: "1px solid var(--line)", alignItems: "center" }}>
                    <span title={isView ? "view" : "action"} style={isView
                      ? { fontSize: 11, color: "var(--muted)" }
                      : { fontSize: 12, color: C.accent, fontWeight: 800 }}>{isView ? "👁" : "✎"}</span>
                    <div style={{ minWidth: 0 }}>
                      <span className="bp-mono" style={{ fontSize: 11.5, fontWeight: 700 }}>{a.userId.slice(0, 8)}</span>{" "}
                      <span style={{ fontSize: 12.5, color: "var(--ink2)" }}>{ACTION_LABEL[a.action] ?? a.action}</span>
                      {a.tenantId && <> <span style={{ color: "var(--muted)" }}>·</span>{" "}
                        <Link to={`/wallets/${encodeURIComponent(a.tenantId)}`} className="bp-mono" style={{ fontSize: 12, fontWeight: 700 }}>{a.tenantId.slice(0, 12)}</Link></>}
                    </div>
                    <span style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600, textAlign: "right" }}>
                      {dateTimeLong(a.at, { tz: true })} · {relative(a.at)}
                    </span>
                  </div>
                );
              })}
              {data?.nextCursor && (
                <button type="button" onClick={() => { setOlder(rows); setCursor(data.nextCursor!); }}
                  style={{ width: "100%", padding: 13, border: "none", background: "var(--subtle)", color: C.accent, fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}>
                  Load earlier entries
                </button>
              )}
            </>
          )}
      </div>
    </>
  );
}

const sel: React.CSSProperties = { padding: "8px 11px", border: "1px solid var(--line2)", borderRadius: 10, background: "var(--subtle)", color: "var(--ink)", fontSize: 12, fontWeight: 700 };
